"use client"

import React, { useEffect, useState, useCallback, useMemo } from "react"
import debounce from "lodash.debounce"
import { api } from "../../services/Axios"
import {
  FaTags,
  FaSearch,
  FaFilter,
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
} from "react-icons/fa"
import PageHeader from "../ui/management/PageHeader"
import StatsCard from "../ui/management/StatsCard"
import Pagination from "../ui/management/Pagination"
import Loader from "../utils/loader/Loader"
import ConfirmationModal from "../utils/avisos/ConfirmationModal"
import MessageModal from "../utils/avisos/MessageModal"
import ReusableModal from "../utils/modals/ReusableModal"

export interface Brand {
  _id: string
  nombre: string
  descripcion?: string
}

function ManageBrands() {
  // Estados para la vista, búsqueda, paginación y modales
  const [isReadOnlyMode, setIsReadOnlyMode] = useState<boolean>(false)
  const [brands, setBrands] = useState<Brand[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalBrands, setTotalBrands] = useState<number>(0)
  const [totalPages, setTotalPages] = useState<number>(1)

  // Estados para confirmación y mensajes
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false)
  const [modalMessage, setModalMessage] = useState<string>("")
  const [modalAction, setModalAction] = useState<(() => void) | null>(null)
  const [showMessageModal, setShowMessageModal] = useState<boolean>(false)
  const [messageContent, setMessageContent] = useState<string>("")
  const [messageType, setMessageType] = useState<"success" | "error">("success")

  // Estados para los campos del formulario
  const [nombre, setNombre] = useState<string>("")
  const [descripcion, setDescripcion] = useState<string>("")
  const [nombreError, setNombreError] = useState<string>("")
  const [isSaving, setIsSaving] = useState<boolean>(false)

  const brandsPerPage = 6

  // Función para obtener marcas con debounce y paginación
  const debouncedFetchBrands = useMemo(
    () =>
      debounce((page: number, search: string) => {
        setIsLoading(true)
        api
          .get(`/api/marcas`, {
            params: {
              page,
              limit: brandsPerPage,
              searchTerm: search || undefined,
            },
          })
          .then((response) => {
            const { marcas, totalPages, totalMarcas } = response.data
            setBrands(marcas)
            setTotalPages(totalPages)
            setTotalBrands(totalMarcas)
            setCurrentPage(page)
          })
          .catch((error) =>
            console.error("Error al obtener marcas:", error),
          )
          .finally(() => setIsLoading(false))
      }, 500),
    [brandsPerPage],
  )

  const fetchBrands = useCallback(
    (page: number, search: string) => {
      debouncedFetchBrands(page, search)
    },
    [debouncedFetchBrands],
  )

  const fetchBrandsWrapper = useCallback(() => {
    fetchBrands(currentPage, searchTerm)
  }, [fetchBrands, currentPage, searchTerm])

  useEffect(() => {
    fetchBrandsWrapper()
  }, [fetchBrandsWrapper])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setCurrentPage(1)
  }

  const clearFilters = () => {
    setSearchTerm("")
    setCurrentPage(1)
    fetchBrands(1, "")
  }

  // Funciones para el modal de confirmación
  const openConfirmationModal = (message: string, action?: () => void) => {
    setModalMessage(message)
    setModalAction(() => action || closeConfirmationModal)
    setConfirmModalOpen(true)
  }

  const closeConfirmationModal = () => {
    setConfirmModalOpen(false)
    setModalAction(null)
    setModalMessage("")
  }

  // Acciones para agregar, ver, editar y eliminar marcas
  const handleViewBrand = (brand: Brand) => {
    setSelectedBrand(brand)
    setNombre(brand.nombre)
    setDescripcion(brand.descripcion || "")
    setIsEditMode(false)
    setIsReadOnlyMode(true)
    setIsModalOpen(true)
  }

  const handleAddBrand = () => {
    setIsEditMode(false)
    setSelectedBrand(null)
    setNombre("")
    setDescripcion("")
    setNombreError("")
    setIsModalOpen(true)
  }

  const handleEditBrand = (brand: Brand) => {
    setSelectedBrand(brand)
    setNombre(brand.nombre)
    setDescripcion(brand.descripcion || "")
    setNombreError("")
    setIsEditMode(true)
    setIsModalOpen(true)
  }

  const handleDeleteBrand = (brandId: string) => {
    openConfirmationModal("¿Está seguro de que desea eliminar esta marca?", () => {
      api
        .delete(`/api/marcas/${brandId}`)
        .then(() => {
          setConfirmModalOpen(false)
          setMessageContent("Marca eliminada exitosamente.")
          setMessageType("success")
          setShowMessageModal(true)
          fetchBrandsWrapper()
        })
        .catch(() => {
          setConfirmModalOpen(false)
          setMessageContent("Error al eliminar la marca.")
          setMessageType("error")
          setShowMessageModal(true)
        })
        .finally(() => setConfirmModalOpen(false))
    })
  }

  const handleConfirmSaveBrand = () => {
    if (!nombre.trim()) {
      setNombreError("Este campo es obligatorio.")
      return
    }
    openConfirmationModal(
      isEditMode
        ? "¿Está seguro de que desea guardar los cambios en esta marca?"
        : "¿Está seguro de que desea agregar esta marca?",
      handleSaveBrand,
    )
  }

  const handleSaveBrand = () => {
    if (isSaving) return; // 🚫 Si ya está guardando, no hace nada
    setIsSaving(true)
    const action = isEditMode && selectedBrand
      ? api.put(`/api/marcas/${selectedBrand._id}`, { nombre, descripcion })
      : api.post("/api/marcas/add", { nombre, descripcion })

    action
      .then(() => {
        setConfirmModalOpen(false)
        setMessageContent(
          isEditMode
            ? "Marca actualizada exitosamente."
            : "Marca agregada exitosamente.",
        )
        setMessageType("success")
        setShowMessageModal(true)
        fetchBrandsWrapper()
        setIsModalOpen(false)
      })
      .catch((error) => {
        const errorMsg =
          error.response?.data?.msg || "Error al guardar la marca."
        setMessageContent(errorMsg)
        setMessageType("error")
        setShowMessageModal(true)
      })
      .finally(() => {
        setIsSaving(false)
        closeConfirmationModal()
      })
  }

  const handleCloseMessageModal = () => {
    setShowMessageModal(false)
    setMessageContent("")
    setConfirmModalOpen(false)
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {isLoading && <Loader />}

      <ConfirmationModal
        isOpen={confirmModalOpen}
        title="Confirmación"
        message={modalMessage}
        onConfirm={modalAction || closeConfirmationModal}
        onCancel={closeConfirmationModal}
      />
      <MessageModal
        isOpen={showMessageModal}
        message={messageContent}
        messageType={messageType}
        onClose={handleCloseMessageModal}
      />

      {/* Encabezado de la página */}
      <PageHeader
        title="Gestión de Marcas"
        subtitle="Administra tus marcas"
        icon={FaTags}
        actionLabel="Agregar Marca"
        onAction={handleAddBrand}
        actionIcon={FaPlus}
      />

      {/* Filtros y búsqueda */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center">
            <FaFilter className="mr-2 text-[#2d53da]" /> Filtros de Marcas
          </h2>
          <button
            onClick={clearFilters}
            className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200"
          >
            <i className="fa-solid fa-circle-xmark mr-2"></i> Borrar Filtros
          </button>
        </div>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Buscar por nombre de marca"
            value={searchTerm}
            onChange={handleSearchChange}
            className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-[#2d53da] focus:border-[#2d53da] transition-colors duration-200"
          />
        </div>
      </div>

      {/* Indicadores */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatsCard
          title="Total Marcas"
          value={totalBrands.toString()}
          description="Marcas existentes"
          icon={FaTags}
          gradient={false}
        />
      </div>

      {/* Lista de marcas */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        {brands.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Descripción
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {brands.map((brand) => (
                  <tr key={brand._id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{brand.nombre}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {brand.descripcion}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          className="bg-blue-100 text-blue-600 hover:bg-blue-200 p-2 rounded-full transition-colors duration-200"
                          onClick={() => handleViewBrand(brand)}
                          title="Ver Marca"
                        >
                          <FaEye />
                        </button>
                        <button
                          className="bg-yellow-100 text-yellow-600 hover:bg-yellow-200 p-2 rounded-full transition-colors duration-200"
                          onClick={() => handleEditBrand(brand)}
                          title="Editar Marca"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="bg-red-100 text-red-600 hover:bg-red-200 p-2 rounded-full transition-colors duration-200"
                          onClick={() => handleDeleteBrand(brand._id)}
                          title="Eliminar Marca"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="bg-gray-100 p-4 rounded-full mb-3">
              <FaTags className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No se encontraron marcas</h3>
            <p className="text-gray-500 mb-4">
              Intenta con otra búsqueda o agrega una nueva marca.
            </p>
            <button
              onClick={handleAddBrand}
              className="flex items-center px-4 py-2 bg-[#2d53da] hover:bg-[#2d53da]/90 text-white rounded-lg transition-colors duration-200"
            >
              <FaPlus className="mr-2" /> Agregar Marca
            </button>
          </div>
        )}
      </div>

      {/* Paginación */}
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      {/* Modal para agregar/editar o ver marca */}
      <ReusableModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setIsReadOnlyMode(false)
        }}
        title={isReadOnlyMode ? "Ver Marca" : isEditMode ? "Editar Marca" : "Agregar Marca"}
        fields={[
          { label: "Nombre", value: nombre, onChange: setNombre, required: true, error: nombreError, readOnly: isReadOnlyMode },
          { label: "Descripción", value: descripcion, onChange: setDescripcion, readOnly: isReadOnlyMode },
        ]}
        onSave={handleConfirmSaveBrand}
        isSaving={isSaving}
        saveButtonText={isEditMode ? "Guardar Cambios" : "Agregar"}
        readOnlyMode={isReadOnlyMode}
      />

      <ConfirmationModal
        isOpen={confirmModalOpen}
        title="Confirmación"
        message={modalMessage}
        onConfirm={modalAction || closeConfirmationModal}
        onCancel={closeConfirmationModal}
        isProcessing={isSaving}
      />

      <MessageModal
        isOpen={showMessageModal}
        message={messageContent}
        messageType={messageType}
        onClose={handleCloseMessageModal}
      />
    </div>
  )
}

export default ManageBrands
